import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listConflicts,
  resolveConflict,
  resolveConflicts,
  type Conflict,
  type DiffRun,
} from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";

/**
 * ConflictInbox is what a refresh kept of the author's words, and what it would
 * have written instead (ADR-0098 §8).
 *
 * **Nothing here is urgent and nothing here is lost.** The refresh already
 * finished and the book already says what the author wrote; this is a queue
 * reviewed at leisure, which is why it is a screen off the book rather than a
 * step in the middle of a press.
 *
 * **The source diff is computed on the server.** `\b` is ASCII-only in
 * JavaScript and fires inside Arabic-script words, and there is no client twin
 * of `textmatch`, so this renders the runs it is given and decides no word
 * boundaries of its own (CLAUDE.md).
 *
 * **Each block of authored text carries `dir="auto"` and nothing inside it
 * does.** An element with `dir="auto"` skips descendants that carry their own
 * direction when it looks for the first strong character, so a `dir` on a run
 * would leave the block with nothing to read and lay a Persian paragraph out
 * left to right.
 *
 * The two versions are **stacked, never joined by an arrow**: a `→` between two
 * Persian runs points from the correction back at the word, and stacking is the
 * better shape on a phone anyway.
 */
export default function ConflictInbox({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();

  const inbox = useQuery({
    queryKey: ["conflicts", bookID],
    queryFn: () => listConflicts(bookID),
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ["conflicts", bookID] });
    // Taking rewrites a chapter, so its text and the run's open count both move.
    void client.invalidateQueries({ queryKey: ["translation", bookID] });
    void client.invalidateQueries({ queryKey: ["chapters", bookID] });
  };

  const settleOne = useMutation({
    mutationFn: ({ id, take }: { id: string; take: boolean }) => resolveConflict(id, take),
    onSuccess: refresh,
  });
  const settleMany = useMutation({
    mutationFn: ({ take, chapterID }: { take: boolean; chapterID?: string }) =>
      resolveConflicts(bookID, take, chapterID),
    onSuccess: refresh,
  });

  const busy = settleOne.isPending || settleMany.isPending;
  const chapters = inbox.data?.chapters ?? [];

  return (
    <section data-testid="conflict-inbox">
      <h2 className="text-sm font-medium">{t.translation.conflictsHeading}</h2>
      <p className="mt-1 mb-3 text-xs text-muted">{t.translation.conflictsNote}</p>

      <MutationError
        testId="conflict-error"
        error={settleOne.error ?? settleMany.error}
        fallback={t.translation.conflictFailed}
      />

      {inbox.data && inbox.data.open === 0 && (
        <p data-testid="conflicts-none" className="text-sm text-muted">
          {t.translation.conflictsNone}
        </p>
      )}

      {/* Whole-book bulk first, because on a long book it is what most people
          want and a queue that can only be walked one item at a time is a queue
          nobody finishes. */}
      {inbox.data && inbox.data.open > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="conflicts-keep-all"
            disabled={busy}
            onClick={() => settleMany.mutate({ take: false })}
            className="rounded-full border border-line px-3 py-1.5 text-xs transition-colors hover:bg-sunk disabled:opacity-50"
          >
            {t.translation.conflictKeepAll}
          </button>
          <button
            type="button"
            data-testid="conflicts-take-all"
            disabled={busy}
            onClick={() => settleMany.mutate({ take: true })}
            className="rounded-full border border-line px-3 py-1.5 text-xs transition-colors hover:bg-sunk disabled:opacity-50"
          >
            {t.translation.conflictTakeAll}
          </button>
        </div>
      )}

      {chapters.map((chapter) => (
        <section key={chapter.chapter_id} data-testid="conflict-chapter" className="mb-6">
          <h3 dir="auto" className="text-sm font-medium">
            {chapter.title}
          </h3>
          {/* A press that outdates a recording names what it outdates
              (ADR-0096's rule, at a new press). A title never does. */}
          {chapter.audio_outdated && chapter.items.some((i) => i.unit !== "title") && (
            <p className="mt-1 text-xs text-muted">{t.translation.conflictAudio(1)}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="chapter-keep-all"
              disabled={busy}
              onClick={() => settleMany.mutate({ take: false, chapterID: chapter.chapter_id })}
              className="rounded-full border border-line px-3 py-1 text-xs transition-colors hover:bg-sunk disabled:opacity-50"
            >
              {t.translation.conflictKeepAll}
            </button>
            <button
              type="button"
              data-testid="chapter-take-all"
              disabled={busy}
              onClick={() => settleMany.mutate({ take: true, chapterID: chapter.chapter_id })}
              className="rounded-full border border-line px-3 py-1 text-xs transition-colors hover:bg-sunk disabled:opacity-50"
            >
              {t.translation.conflictTakeAll}
            </button>
          </div>

          {chapter.items.map((item) => (
            <Item
              key={item.id}
              item={item}
              busy={busy}
              onSettle={(take) => settleOne.mutate({ id: item.id, take })}
            />
          ))}
        </section>
      ))}
    </section>
  );
}

function Item({
  item,
  busy,
  onSettle,
}: {
  item: Conflict;
  busy: boolean;
  onSettle: (take: boolean) => void;
}) {
  const t = useT();
  const label =
    item.unit === "title"
      ? t.translation.conflictTitleUnit
      : item.unit === "chapter"
        ? t.translation.conflictChapterUnit
        : null;

  return (
    <article
      data-testid="conflict-item"
      className="mt-3 rounded-card border border-line bg-surface p-3"
    >
      {label && <p className="mb-2 text-xs font-medium text-muted">{label}</p>}

      {/* Why the machine wanted to change it, which is the thing that makes the
          decision possible — the two translations alone cannot be judged. */}
      <p className="text-xs text-muted">{t.translation.conflictSourceChanged}</p>
      <p dir="auto" className="mt-1 text-sm leading-relaxed">
        {item.source_diff.map((run, i) => (
          <Run key={i} run={run} />
        ))}
      </p>

      <p className="mt-3 text-xs text-muted">{t.translation.conflictYours}</p>
      <p dir="auto" data-testid="conflict-kept" className="mt-1 text-sm leading-relaxed">
        {item.kept}
      </p>

      <p className="mt-3 text-xs text-muted">{t.translation.conflictProposed}</p>
      <p dir="auto" data-testid="conflict-proposed" className="mt-1 text-sm leading-relaxed">
        {item.proposed}
      </p>

      {item.superseded && (
        <p data-testid="conflict-superseded" className="mt-3 text-xs text-muted">
          {t.translation.conflictSuperseded}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="conflict-keep"
          disabled={busy}
          onClick={() => onSettle(false)}
          className="rounded-full border border-line px-3 py-1.5 text-xs transition-colors hover:bg-sunk disabled:opacity-50"
        >
          {t.translation.conflictKeep}
        </button>
        {/* An item whose words the author has since replaced cannot be taken:
            the alternative is about text that is no longer in the chapter. */}
        {!item.superseded && (
          <button
            type="button"
            data-testid="conflict-take"
            disabled={busy}
            onClick={() => onSettle(true)}
            className="rounded-full border border-sage bg-sage-soft px-3 py-1.5 text-xs font-medium text-sage-deep transition-colors hover:bg-sage-soft/70 disabled:opacity-50"
          >
            {t.translation.conflictTake}
          </button>
        )}
      </div>
    </article>
  );
}

/** Run is one stretch of the server's word diff. It carries no `dir` of its
 *  own: the block above it has `dir="auto"`, and a direction here would be
 *  exactly what stops that working. */
function Run({ run }: { run: DiffRun }) {
  if (run.kind === "removed") {
    return <span className="text-muted line-through">{run.text}</span>;
  }
  if (run.kind === "added") {
    return <span className="font-medium text-sage-deep">{run.text}</span>;
  }
  return <span>{run.text}</span>;
}
